/**
 * MediaLightbox.js — S20 — full-screen media preview with download.
 *
 * Tapping an image/video bubble opens this overlay. Renders the asset
 * at its natural size (capped to viewport) + a Download button that
 * triggers an anonymous anchor with the `download` attribute. Close
 * via X button, background tap, or Escape key.
 *
 * Deliberately kept minimal — no zoom, no pinch, no swipe-between. The
 * S20 goal is reliable preview + download; richer gallery UX is a
 * future enhancement. Uses inline styles so it works without CSS
 * changes to the bundle.
 */
import { html } from 'htm/preact';
import { useEffect } from 'preact/hooks';

export function MediaLightbox({ url, mimeType = '', filename = '', onClose }) {
  useEffect(() => {
    if (!url) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while the overlay is up.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [url, onClose]);

  if (!url) return null;
  const isVideo = mimeType.startsWith('video/') || /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
  const suggestedName = filename || (isVideo ? 'video' : 'image');

  // Clicking the backdrop closes; clicking the asset itself doesn't.
  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose?.(); };

  return html`
    <div
      onClick=${onBackdrop}
      style="position:fixed;inset:0;background:rgba(0,0,0,0.92);z-index:9999;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:max(env(safe-area-inset-top),16px) 16px max(env(safe-area-inset-bottom),16px)"
      role="dialog"
      aria-modal="true"
    >
      <!-- Close -->
      <button
        onClick=${onClose}
        aria-label="Close"
        style="position:absolute;top:max(env(safe-area-inset-top),12px);right:12px;width:40px;height:40px;border-radius:50%;background:rgba(255,255,255,0.1);border:none;color:#fff;font-size:1.4rem;cursor:pointer;display:flex;align-items:center;justify-content:center;-webkit-tap-highlight-color:transparent"
      >×</button>

      <!-- Asset -->
      <div style="max-width:100%;max-height:calc(100vh - 120px);display:flex;align-items:center;justify-content:center">
        ${isVideo
          ? html`<video src=${url} controls autoplay playsinline style="max-width:100%;max-height:100%;background:#000;border-radius:6px" />`
          : html`<img src=${url} alt=${suggestedName} style="max-width:100%;max-height:100%;object-fit:contain;border-radius:6px" />`}
      </div>

      <!-- Download -->
      <a
        href=${url}
        download=${suggestedName}
        target="_blank"
        rel="noopener"
        style="margin-top:14px;display:inline-flex;align-items:center;gap:8px;padding:10px 18px;background:rgba(37,211,102,0.9);color:#0b1220;text-decoration:none;font-size:0.9rem;font-weight:600;border-radius:999px;-webkit-tap-highlight-color:transparent"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
        Download
      </a>
    </div>
  `;
}
